import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RotateCcw, Home, BookMarked, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { MapboxWorldMap } from "../../components/child/MapboxWorldMap";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

const REGION_ORDER = ["Noord", "Oost", "Zuid", "West", "Midden"];
const shuffle = (a) => { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]]; } return r; };

export default function WorldMapGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const continent = location.state?.continent || "Europa";
  const { theme, terms } = useTheme();
  const { refresh } = useAuth();

  const [all, setAll] = useState(null);          // alle speelbare landen in dit werelddeel
  const [region, setRegion] = useState("Heel");
  const [tray, setTray] = useState([]);          // resterende codes (geschud)
  const [placedCount, setPlacedCount] = useState(0);
  const [drag, setDrag] = useState(null);
  const [ready, setReady] = useState(false);
  const [result, setResult] = useState(null);

  const mapRef = useRef();
  const draggingRef = useRef(false);
  const curRef = useRef(null);
  const lock = useRef(false);
  const perfectRef = useRef(0);
  const wrongOnce = useRef(new Set());
  const placedRef = useRef([]);
  const totalRef = useRef(0);

  useEffect(() => {
    api.get(`/child/world/countries?continent=${encodeURIComponent(continent)}`)
      .then(({ data }) => setAll(data.countries))
      .catch((err) => toast.error(apiErr(err.response?.data?.detail)));
  }, [continent]);

  const idToCode = useMemo(() => {
    const m = {}; (all || []).forEach((c) => { m[c.id] = c.code; }); return m;
  }, [all]);

  const byCode = useMemo(() => {
    const m = {}; (all || []).forEach((c) => { m[c.code] = c; }); return m;
  }, [all]);

  const regions = useMemo(() => {
    if (!all) return [];
    const counts = {};
    all.forEach((c) => { counts[c.region] = (counts[c.region] || 0) + 1; });
    return REGION_ORDER.filter((r) => counts[r] >= 2);
  }, [all]);

  const activeCodes = useMemo(() => {
    if (!all) return [];
    return all.filter((c) => region === "Heel" || c.region === region).map((c) => c.code);
  }, [all, region]);

  // Start/herstart een ronde voor de gekozen regio
  const setupRound = (codes) => {
    perfectRef.current = 0; wrongOnce.current = new Set(); placedRef.current = []; totalRef.current = codes.length;
    setPlacedCount(0); setResult(null);
    setTray(shuffle(codes));
    if (mapRef.current) {
      mapRef.current.clearAll();
      mapRef.current.setPlayable(codes);
      mapRef.current.fitToCodes(codes);
      setTimeout(() => mapRef.current?.fitToCodes(codes), 350);
    }
  };

  useEffect(() => {
    if (ready && activeCodes.length) setupRound(activeCodes);
    // eslint-disable-next-line
  }, [ready, region, all]);

  const finishGame = async () => {
    try {
      const { data } = await api.post("/child/world/map/finish", {
        continent, region: region === "Heel" ? null : region,
        correct: perfectRef.current, total: totalRef.current, placed: placedRef.current,
      });
      setResult(data);
      if (totalRef.current && perfectRef.current / totalRef.current >= 0.7) sfx.win(); else sfx.coin();
      refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const handleDrop = (code) => {
    const cur = curRef.current;
    if (!cur || lock.current) return;
    if (code === cur) {
      lock.current = true;
      mapRef.current.markCorrect(cur);
      sfx.correct();
      placedRef.current.push(cur);
      if (!wrongOnce.current.has(cur)) perfectRef.current += 1;
      const remaining = tray.filter((c) => c !== cur);
      setTray(remaining);
      setPlacedCount((n) => n + 1);
      setTimeout(() => { lock.current = false; if (remaining.length === 0) finishGame(); }, 120);
    } else {
      mapRef.current.flashWrong(code);
      wrongOnce.current.add(cur);
      sfx.wrong();
    }
  };

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      setDrag({ x: e.clientX, y: e.clientY });
      mapRef.current?.setHighlight(mapRef.current?.hitTest(e.clientX, e.clientY));
    };
    const up = (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const code = mapRef.current?.hitTest(e.clientX, e.clientY);
      setDrag(null);
      mapRef.current?.setHighlight(null);
      handleDrop(code);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    // eslint-disable-next-line
  }, [tray]);

  const startDrag = (e, code) => {
    if (lock.current) return;
    e.preventDefault();
    curRef.current = code;
    draggingRef.current = true;
    setDrag({ x: e.clientX, y: e.clientY });
  };

  const muted = { color: "var(--t-muted)" };
  const total = totalRef.current || activeCodes.length;
  const pct = total ? Math.round((placedCount / total) * 100) : 0;

  // ---------- RESULT ----------
  if (result) {
    const great = totalRef.current && perfectRef.current / totalRef.current >= 0.7;
    return (
      <ThemedShell>
        <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
          {[...Array(34)].map((_, i) => (
            <motion.div key={i} initial={{ y: -40, x: Math.random() * 100 + "vw", opacity: 1, rotate: 0 }}
              animate={{ y: "110vh", rotate: 360 }} transition={{ duration: 2.2 + Math.random() * 1.5, delay: Math.random() * 0.5, ease: "easeIn" }}
              className="absolute w-2.5 h-3.5 rounded-sm" style={{ background: ["var(--t-primary)", "var(--t-accent)", "var(--t-success)"][i % 3] }} />
          ))}
        </div>
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-16" data-testid="completion-celebration-modal">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <ThemedCard className="p-8 text-center">
              <motion.div className="text-6xl mb-3" animate={{ scale: [0, 1.3, 1], rotate: [-20, 10, 0] }} transition={{ duration: 0.8 }}>{great ? "🌍" : theme.mascot}</motion.div>
              <h1 className="font-heading text-3xl font-black themed-heading">{region === "Heel" ? continent : `${region}-${continent}`} voltooid!</h1>
              <p className="mt-2 font-semibold" style={muted}>Je plaatste alle {totalRef.current} landen — <b>{perfectRef.current}</b> in één keer goed!</p>
              <div className="flex justify-center gap-3 flex-wrap mt-6">
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="map-xp"><span className="font-heading font-extrabold">+<CountUp value={result?.xp_earned || 0} delay={250} /> {terms.xp}</span></div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="map-coins"><span className="text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold">+<CountUp value={result?.coins_earned || 0} delay={400} /> {terms.coins}</span></div>
              </div>
              {result?.new_badges?.length > 0 && (
                <div className="mt-6 p-4 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="map-badges">
                  <p className="font-heading font-bold">Nieuwe {terms.badges.toLowerCase()}! 🎉</p>
                  <div className="flex justify-center gap-2 mt-3 flex-wrap">{result.new_badges.map((b) => <span key={b.id} className="px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>{b.title}</span>)}</div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button onClick={() => setupRound(activeCodes)} className="flex-1 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="map-again-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een keer</Button>
                <Button onClick={() => navigate("/leerling/paspoort")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="map-passport-btn"><BookMarked className="w-4 h-4 mr-1.5" /> Paspoort</Button>
                <Button onClick={() => navigate("/leerling/wereldreis")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="map-home-btn"><Home className="w-4 h-4 mr-1.5" /> Wereldreis</Button>
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      </ThemedShell>
    );
  }

  // ---------- PLAY ----------
  return (
    <ThemedShell>
      <div className="fixed inset-0 flex flex-col overflow-hidden" data-testid="wereldreis-game-container" style={{ background: "var(--t-surface)" }}>
        <div className="relative flex-1">
          <MapboxWorldMap ref={mapRef} idToCode={idToCode} onReady={() => setReady(true)} />

          {/* Top bar: terug + voortgang */}
          <div className="absolute top-0 inset-x-0 z-20 p-3 flex items-center gap-3 pointer-events-none">
            <button onClick={() => navigate("/leerling/wereldreis")} data-testid="map-back-btn" className="pointer-events-auto w-11 h-11 flex items-center justify-center rounded-full border-2 shadow-sm active:scale-95 transition" style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-text)" }}><ArrowLeft className="w-5 h-5" /></button>
            <div className="pointer-events-auto flex-1 max-w-md h-9 rounded-full border-2 overflow-hidden relative" style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)" }} data-testid="game-progress-bar">
              <motion.div className="h-full" style={{ background: "var(--t-success)" }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
              <div className="absolute inset-0 flex items-center justify-center text-xs font-heading font-black tracking-wide" style={{ color: "var(--t-text)" }} data-testid="map-progress">{placedCount}/{total} landen</div>
            </div>
          </div>

          {/* Regiofilter */}
          <div className="absolute top-16 inset-x-0 z-20 px-3 flex gap-2 overflow-x-auto scrollbar-hide pointer-events-auto pb-1">
            {["Heel", ...regions].map((r) => {
              const on = region === r;
              return (
                <button key={r} onClick={() => { if (!lock.current) setRegion(r); }} data-testid={`region-filter-${r}`}
                  className="whitespace-nowrap px-4 py-2 rounded-full border-2 font-heading font-bold text-sm shadow-sm active:scale-95 transition shrink-0"
                  style={on ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                  {r === "Heel" ? "Heel continent" : r}
                </button>
              );
            })}
          </div>

          {!ready && <div className="absolute inset-0 flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div>}
        </div>

        {/* Vlaggenbalk */}
        <div className="relative z-30 border-t-4 backdrop-blur-xl" style={{ background: `color-mix(in srgb, var(--t-surface2) 88%, transparent)`, borderColor: "var(--t-border)" }} data-testid="flag-tray">
          <p className="px-5 pt-3 text-xs font-bold uppercase tracking-wider" style={muted}>Sleep elke vlag naar het juiste land</p>
          <div className="px-4 pb-5 pt-2 flex gap-3 overflow-x-auto scrollbar-hide items-center" style={{ minHeight: 92 }}>
            <AnimatePresence mode="popLayout">
              {tray.length === 0 ? (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mx-auto flex items-center gap-2 font-heading text-lg" style={muted}><Check className="w-5 h-5" style={{ color: "var(--t-success)" }} /> Alle landen geplaatst!</motion.div>
              ) : tray.map((code) => (
                <motion.div key={code} layout initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }} transition={{ type: "spring", stiffness: 380, damping: 26 }}
                  onPointerDown={(e) => startDrag(e, code)} data-testid={`draggable-flag-${code}`}
                  className="shrink-0 rounded-xl overflow-hidden border-2 shadow-md cursor-grab active:cursor-grabbing"
                  style={{ borderColor: "var(--t-border)", touchAction: "none", width: 74, height: 52, opacity: drag && curRef.current === code ? 0.3 : 1 }}>
                  <img src={`https://flagcdn.com/w160/${code}.png`} alt={byCode[code]?.name} draggable={false} className="w-full h-full object-cover pointer-events-none" />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Sleep-spook */}
      {drag && curRef.current && (
        <div className="fixed z-[70] rounded-lg overflow-hidden border-2" style={{ left: drag.x - 38, top: drag.y - 27, width: 76, height: 54, borderColor: "var(--t-accent)", pointerEvents: "none", boxShadow: "0 18px 26px rgba(0,0,0,.28)", transform: "rotate(-5deg) scale(1.1)" }}>
          <img src={`https://flagcdn.com/w160/${curRef.current}.png`} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </ThemedShell>
  );
}
